// N-body gravity, lifted from the solar-system widget: G = 0.6, softened inverse square,
// units where the Sun sits near the origin and planets orbit at radius 0.2 to 0.9.

export const G = 0.6;
export const E2 = 0.0015;
export const ESCAPE_R2 = 6;      // beyond sqrt(6) a body has left the system
export const DT = 0.0025;

export interface Body {
  x: number; y: number; vx: number; vy: number; m: number;
  kind: 0 | 1;                  // 0 = sun, 1 = planet
  tr: [number, number][];
  angle: number;                // accumulated orbit angle around the sun (radians)
  rMin: number; rMax: number;   // radius range since launch
  laps: number;
  escaped: boolean;
}

export function makeSun(m: number): Body { return { x: 0, y: 0, vx: 0, vy: 0, m, kind: 0, tr: [], angle: 0, rMin: 0, rMax: 0, laps: 0, escaped: false }; }

/** a planet at radius r and angle a with a velocity scale (1 = circular) */
export function makePlanet(sun: Body, r: number, a: number, vScale = 1, m = 0.004): Body {
  const v = Math.sqrt(G * sun.m / r) * vScale;
  const x = sun.x + r * Math.cos(a), y = sun.y + r * Math.sin(a);
  return { x, y, vx: sun.vx - v * Math.sin(a), vy: sun.vy + v * Math.cos(a), m, kind: 1, tr: [], angle: 0, rMin: r, rMax: r, laps: 0, escaped: false };
}

export function circularSpeed(sunMass: number, r: number): number { return Math.sqrt(G * sunMass / r); }

export function step(B: Body[], dt = DT) {
  const n = B.length;
  for (let i = 0; i < n; i++) {
    let ax = 0, ay = 0; const a = B[i];
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const b = B[j], dx = b.x - a.x, dy = b.y - a.y, d2 = dx * dx + dy * dy + E2, f = G * b.m / (d2 * Math.sqrt(d2));
      ax += f * dx; ay += f * dy;
    }
    a.vx += ax * dt; a.vy += ay * dt;
  }
  const sun = B[0];
  for (let i = 0; i < n; i++) {
    const b = B[i];
    const px = b.x - sun.x, py = b.y - sun.y;
    b.x += b.vx * dt; b.y += b.vy * dt;
    if (b.kind === 1) {
      const qx = b.x - sun.x, qy = b.y - sun.y;
      const da = Math.atan2(px * qy - py * qx, px * qx + py * qy);
      b.angle += da;
      b.laps = Math.floor(Math.abs(b.angle) / (Math.PI * 2));
      const r = Math.hypot(qx, qy);
      if (r < b.rMin) b.rMin = r; if (r > b.rMax) b.rMax = r;
      if (qx * qx + qy * qy > ESCAPE_R2) b.escaped = true;
    }
  }
}

/** stable = at least `laps` laps with the orbit's radius range within `tol` of its mean */
export function isStable(b: Body, laps = 3, tol = 0.4): boolean {
  if (b.escaped || b.laps < laps) return false;
  const mean = (b.rMin + b.rMax) / 2;
  return (b.rMax - b.rMin) / mean <= tol;
}
